/**
 * Voice Selection Service for Vocalira
 * Provides consistent male/female voice selection across all devices
 * 
 * HYBRID MODEL:
 * - Free users: Web Speech API (browser native, robotic)
 * - Premium users: OpenAI TTS API (natural, high quality)
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache for premium audio to avoid repeated API calls
const audioCache = new Map();
const MAX_CACHE_SIZE = 50;

// Track currently playing premium audio for stopSpeaking()
let activePremiumAudio = null;

// Male voice identifiers (prioritized order)
const MALE_VOICE_NAMES = [
  // Google voices
  'google uk english male',
  'google us english male', 
  'google male',
  // Microsoft voices
  'microsoft david',
  'microsoft mark',
  'microsoft george',
  'david',
  'mark',
  'george',
  // Apple voices
  'alex',
  'daniel',
  'fred',
  'tom',
  'oliver',
  // Generic male identifiers
  'male',
  'homme',
  'hombre',
  'mann',
  'masculino',
  // Specific language male voices
  'jorge', // Spanish
  'diego', // Spanish
  'thomas', // French/German
  'hans', // German
  'luca', // Italian
];

// Female voice identifiers (prioritized order)
const FEMALE_VOICE_NAMES = [
  // Google voices
  'google uk english female',
  'google us english female',
  'google female',
  // Microsoft voices
  'microsoft zira',
  'microsoft hazel',
  'microsoft susan',
  'zira',
  'hazel',
  'susan',
  // Apple voices
  'samantha',
  'victoria',
  'karen',
  'moira',
  'fiona',
  // Generic female identifiers
  'female',
  'femme',
  'mujer',
  'frau',
  'feminino',
  // Specific language female voices
  'monica', // Spanish
  'paulina', // Spanish
  'amelie', // French
  'anna', // German
  'alice', // Italian
];

// Premium/natural voice keywords
const PREMIUM_KEYWORDS = [
  'premium',
  'enhanced',
  'natural',
  'neural',
  'wavenet',
  'standard',
];

// Language codes mapping
const LANGUAGE_CODES = {
  'English': 'en',
  'Spanish': 'es',
  'French': 'fr',
  'German': 'de',
  'Chinese': 'zh',
  'Italian': 'it',
  'Portuguese': 'pt',
  'Japanese': 'ja',
  'Korean': 'ko',
};

/**
 * Get all available voices from the browser
 */
export const getAvailableVoices = () => {
  if (!('speechSynthesis' in window)) {
    console.warn('Speech synthesis not supported');
    return [];
  }
  return window.speechSynthesis.getVoices();
};

/**
 * Filter voices by language
 */
export const filterVoicesByLanguage = (voices, language) => {
  const langCode = LANGUAGE_CODES[language] || language.substring(0, 2).toLowerCase();
  
  return voices.filter(voice => {
    const voiceLang = voice.lang.toLowerCase();
    return voiceLang.startsWith(langCode) || 
           voiceLang.includes(langCode) ||
           voice.lang.split('-')[0].toLowerCase() === langCode;
  });
};

/**
 * Score a voice based on gender preference and quality
 */
const scoreVoice = (voice, preferredGender = 'male') => {
  const nameLower = voice.name.toLowerCase();
  let score = 0;
  
  const genderNames = preferredGender === 'male' ? MALE_VOICE_NAMES : FEMALE_VOICE_NAMES;
  const oppositeGenderNames = preferredGender === 'male' ? FEMALE_VOICE_NAMES : MALE_VOICE_NAMES;
  
  // Check for explicit gender match (highest priority)
  for (let i = 0; i < genderNames.length; i++) {
    if (nameLower.includes(genderNames[i])) {
      score += 100 - i; // Earlier in list = higher score
      break;
    }
  }
  
  // Penalize opposite gender
  for (const name of oppositeGenderNames) {
    if (nameLower.includes(name)) {
      score -= 200;
      break;
    }
  }
  
  // Bonus for premium/natural voices
  for (const keyword of PREMIUM_KEYWORDS) {
    if (nameLower.includes(keyword)) {
      score += 20;
      break;
    }
  }
  
  // Bonus for local voices (usually better quality)
  if (voice.localService) {
    score += 10;
  }
  
  // Bonus for default voice
  if (voice.default) {
    score += 5;
  }
  
  return score;
};

/**
 * Select the best voice for given language and gender
 * @param {string} language - Target language (e.g., 'English', 'Spanish')
 * @param {string} gender - 'male' or 'female'
 * @param {boolean} strict - If true, returns null if no matching gender found
 * @returns {SpeechSynthesisVoice|null}
 */
export const selectVoice = (language, gender = 'male', strict = false) => {
  const voices = getAvailableVoices();
  
  if (voices.length === 0) {
    console.warn('No voices available');
    return null;
  }
  
  // Filter by language
  let languageVoices = filterVoicesByLanguage(voices, language);
  
  // If no voices for this language, try English as fallback
  if (languageVoices.length === 0) {
    console.warn(`No voices for ${language}, falling back to English`);
    languageVoices = filterVoicesByLanguage(voices, 'English');
  }
  
  // If still no voices, use all available
  if (languageVoices.length === 0) {
    languageVoices = voices;
  }
  
  // Score and sort voices
  const scoredVoices = languageVoices.map(voice => ({
    voice,
    score: scoreVoice(voice, gender)
  })).sort((a, b) => b.score - a.score);
  
  // In strict mode, only return if score is positive (meaning gender matched)
  if (strict && scoredVoices[0]?.score <= 0) {
    console.warn(`No ${gender} voice found for ${language} in strict mode`);
    return null;
  }
  
  const selected = scoredVoices[0]?.voice || null;
  
  if (selected) {
    console.log(`Selected voice: ${selected.name} (${selected.lang}) for ${language}/${gender}, score: ${scoredVoices[0].score}`);
  }
  
  return selected;
};

/**
 * Select male voice with strict fallback
 * Specifically for lessons and listening practice
 */
export const selectMaleVoice = (language) => {
  const voices = getAvailableVoices();
  const languageVoices = filterVoicesByLanguage(voices, language);
  
  // Try to find explicit male voice
  for (const maleName of MALE_VOICE_NAMES) {
    const found = languageVoices.find(v => 
      v.name.toLowerCase().includes(maleName)
    );
    if (found) {
      console.log(`Found explicit male voice: ${found.name}`);
      return found;
    }
  }
  
  // Fallback: find voice that is NOT female
  const nonFemaleVoice = languageVoices.find(v => {
    const nameLower = v.name.toLowerCase();
    return !FEMALE_VOICE_NAMES.some(f => nameLower.includes(f));
  });
  
  if (nonFemaleVoice) {
    console.log(`Using non-female voice as fallback: ${nonFemaleVoice.name}`);
    return nonFemaleVoice;
  }
  
  // Last resort: any voice for the language
  if (languageVoices.length > 0) {
    console.log(`Using first available voice: ${languageVoices[0].name}`);
    return languageVoices[0];
  }
  
  // Absolute fallback: any English male voice
  const englishVoices = filterVoicesByLanguage(voices, 'English');
  for (const maleName of MALE_VOICE_NAMES) {
    const found = englishVoices.find(v => 
      v.name.toLowerCase().includes(maleName)
    );
    if (found) {
      return found;
    }
  }
  
  return voices[0] || null;
};

/**
 * Select female voice with strict fallback
 */
export const selectFemaleVoice = (language) => {
  const voices = getAvailableVoices();
  const languageVoices = filterVoicesByLanguage(voices, language);
  
  // Try to find explicit female voice
  for (const femaleName of FEMALE_VOICE_NAMES) {
    const found = languageVoices.find(v => 
      v.name.toLowerCase().includes(femaleName)
    );
    if (found) {
      console.log(`Found explicit female voice: ${found.name}`);
      return found;
    }
  }
  
  // Fallback: find voice that is NOT male
  const nonMaleVoice = languageVoices.find(v => {
    const nameLower = v.name.toLowerCase();
    return !MALE_VOICE_NAMES.some(m => nameLower.includes(m));
  });
  
  if (nonMaleVoice) {
    console.log(`Using non-male voice as fallback: ${nonMaleVoice.name}`);
    return nonMaleVoice;
  }
  
  return languageVoices[0] || voices[0] || null;
};

/**
 * Speak text with specified voice settings - NATURAL HUMAN-LIKE VERSION
 * @param {string} text - Text to speak
 * @param {Object} options - Options
 * @param {string} options.language - Target language
 * @param {string} options.gender - 'male' or 'female'
 * @param {number} options.rate - Speech rate (0.5 - 2.0)
 * @param {number} options.pitch - Voice pitch (0.5 - 2.0)
 * @param {boolean} options.strictGender - Force gender match for lessons
 * @param {function} options.onStart - Callback when speech starts
 * @param {function} options.onEnd - Callback when speech ends
 * @param {function} options.onError - Callback on error
 */
export const speak = (text, options = {}) => {
  const {
    language = 'English',
    gender = 'male',
    rate = 0.92,  // Slightly slower than normal for natural feel
    pitch = 1.0,
    strictGender = false,
    onStart,
    onEnd,
    onError
  } = options;
  
  if (!('speechSynthesis' in window)) {
    console.error('Speech synthesis not supported');
    onError?.('Speech synthesis not supported');
    return null;
  }
  
  // Cancel any ongoing speech
  window.speechSynthesis.cancel();
  
  const utterance = new SpeechSynthesisUtterance(text);
  
  // Select the best quality voice for the language and gender
  let voice;
  if (strictGender) {
    voice = gender === 'male' ? selectMaleVoice(language) : selectFemaleVoice(language);
  } else {
    voice = selectVoice(language, gender, false);
  }
  
  if (voice) {
    utterance.voice = voice;
    utterance.lang = voice.lang;
    console.log(`Using voice: ${voice.name} (${voice.lang})`);
  }
  
  // NATURAL HUMAN-LIKE VOICE SETTINGS
  // Key insight: Less extreme pitch changes + moderate rate = more natural
  // The previous settings were too extreme (0.72 pitch) which made it sound robotic
  
  if (gender === 'male') {
    // Natural male voice - authoritative but friendly
    // Pitch between 0.85-0.95 sounds more human than 0.72
    utterance.pitch = 0.88;
    utterance.rate = rate * 0.95;  // Slightly measured pace
  } else {
    // Natural female voice - warm and clear
    // Pitch between 1.0-1.05 sounds natural
    utterance.pitch = 1.02;
    utterance.rate = rate * 0.98;
  }
  
  // Full volume for clarity
  utterance.volume = 1.0;
  
  if (onStart) utterance.onstart = onStart;
  if (onEnd) utterance.onend = onEnd;
  if (onError) utterance.onerror = (e) => onError(e.error);
  
  // Small delay to ensure voice is ready
  setTimeout(() => {
    window.speechSynthesis.speak(utterance);
  }, 50);
  
  return utterance;
};

/**
 * Preload voices (call on app init)
 */
export const preloadVoices = () => {
  return new Promise((resolve) => {
    const voices = getAvailableVoices();
    if (voices.length > 0) {
      resolve(voices);
      return;
    }
    
    // Wait for voices to load
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        resolve(getAvailableVoices());
      };
      
      // Timeout fallback
      setTimeout(() => {
        resolve(getAvailableVoices());
      }, 1000);
    } else {
      resolve([]);
    }
  });
};

/**
 * Check if premium TTS is available for user
 * @returns {Promise<boolean>}
 */
export const isPremiumTTSAvailable = async () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    const response = await fetch(`${API}/plan/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) return false;
    const data = await response.json();
    return data.plan === 'premium';
  } catch {
    return false;
  }
};

/**
 * Generate cache key for audio
 */
const getCacheKey = (text, voice, speed) => {
  return `${text.substring(0, 50)}_${voice}_${speed}`;
};

/**
 * Speak using OpenAI TTS (Premium users only)
 * @param {string} text - Text to speak
 * @param {Object} options - Options
 * @returns {Promise<HTMLAudioElement|null>}
 */
export const speakPremium = async (text, options = {}) => {
  const {
    gender = 'female',
    speed = 1.0,
    onStart,
    onEnd,
    onError
  } = options;
  
  const token = localStorage.getItem('token');
  if (!token) {
    onError?.('Não autenticado');
    return null;
  }
  
  // Map gender to OpenAI voice
  const voice = gender === 'male' ? 'onyx' : 'nova';
  const cacheKey = getCacheKey(text, voice, speed);
  
  // Check cache first
  if (audioCache.has(cacheKey)) {
    const cachedAudio = audioCache.get(cacheKey);
    const audio = new Audio(cachedAudio);
    audio.onplay = onStart;
    audio.onended = onEnd;
    audio.onerror = () => onError?.('Erro ao reproduzir áudio');
    audio.play().catch(e => onError?.(e.message));
    return audio;
  }
  
  try {
    const response = await fetch(`${API}/tts/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ text, voice, speed })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erro ao gerar áudio');
    }
    
    const data = await response.json();
    const audioSrc = `data:audio/mp3;base64,${data.audio}`;
    
    // Track TTS usage for cost monitoring
    try {
      fetch(`${API}/insights/track/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text_length: text.length, voice })
      });
    } catch (e) {
      // Non-blocking tracking
    }
    
    // Cache the audio (limit cache size)
    if (audioCache.size >= MAX_CACHE_SIZE) {
      const firstKey = audioCache.keys().next().value;
      audioCache.delete(firstKey);
    }
    audioCache.set(cacheKey, audioSrc);
    
    // Play audio
    const audio = new Audio(audioSrc);
    audio.onplay = onStart;
    audio.onended = () => {
      activePremiumAudio = null;
      onEnd?.();
    };
    audio.onerror = () => {
      activePremiumAudio = null;
      onError?.('Erro ao reproduzir áudio');
    };
    
    // Track for stopSpeaking()
    activePremiumAudio = audio;
    
    await audio.play();
    return audio;
    
  } catch (error) {
    console.error('Premium TTS error:', error);
    onError?.(error.message);
    return null;
  }
};

/**
 * Remove emojis from text to prevent TTS from reading them
 * This ensures clean, natural audio without awkward pauses
 * @param {string} text - Text that may contain emojis
 * @returns {string} - Clean text without emojis
 */
const stripEmojis = (text) => {
  if (!text) return '';
  
  // Comprehensive emoji regex pattern
  const emojiPattern = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F910}-\u{1F96B}]|[\u{1F980}-\u{1F9E0}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]/gu;
  
  // Remove emojis
  let cleaned = text.replace(emojiPattern, '');
  
  // Also remove common emoji-like characters and symbols
  cleaned = cleaned.replace(/[😀-🙏💀-💯🌀-🗿🚀-🛿🤀-🧿🩰-🪿]/gu, '');
  
  // Clean up extra spaces that may result from emoji removal
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  
  return cleaned;
};

/**
 * Hybrid speak function - uses Premium TTS if available, falls back to Web Speech
 * @param {string} text - Text to speak
 * @param {Object} options - Options
 * @returns {Promise<HTMLAudioElement|SpeechSynthesisUtterance|null>}
 */
export const speakHybrid = async (text, options = {}) => {
  const {
    language = 'English',
    gender = 'female',
    speed = 1.0,
    forceFree = false,  // Force Web Speech API even for premium
    onStart,
    onEnd,
    onError
  } = options;
  
  // STRIP EMOJIS before TTS to prevent awkward pauses
  const cleanText = stripEmojis(text);
  
  // Don't speak if text is empty after cleaning
  if (!cleanText) {
    console.log('Text empty after emoji removal, skipping TTS');
    onEnd?.();
    return null;
  }
  
  // Check if should use premium
  if (!forceFree) {
    const isPremium = await isPremiumTTSAvailable();
    
    if (isPremium) {
      console.log('Using Premium OpenAI TTS');
      return speakPremium(cleanText, { gender, speed, onStart, onEnd, onError });
    }
  }
  
  // Fallback to Web Speech API
  console.log('Using Web Speech API (free)');
  return speak(cleanText, {
    language,
    gender,
    rate: speed * 0.92,  // Adjust for Web Speech
    onStart,
    onEnd,
    onError
  });
};

/**
 * Stop any currently playing speech (both Web Speech and premium audio)
 */
export const stopSpeaking = () => {
  // Stop Web Speech API
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  
  // Stop premium audio if playing
  if (activePremiumAudio) {
    activePremiumAudio.pause();
    activePremiumAudio.currentTime = 0;
    activePremiumAudio = null;
  }
};

// Default export at the end to avoid circular reference
export default {
  getAvailableVoices,
  filterVoicesByLanguage,
  selectVoice,
  selectMaleVoice,
  selectFemaleVoice,
  speak,
  preloadVoices,
  speakPremium,
  speakHybrid,
  stopSpeaking,
  isPremiumTTSAvailable,
  LANGUAGE_CODES,
};
